/**
 * Countries Service
 * Handles country metadata CRUD operations
 * Read operations are public, write operations require admin authorization
 */

import api from './apiClient';

/**
 * @typedef {Object} FlagImage
 * @property {number} id - File ID
 * @property {string} fileName - Original filename
 * @property {string} contentType - MIME type
 * @property {string} relativeUrl - URL path to access the file
 * @property {string} [altText] - Alternative text for accessibility
 */

/**
 * @typedef {Object} Country
 * @property {number} id - Country ID
 * @property {string} name - Country name
 * @property {string} language - Primary language (e.g., "English", "Deutsch", "Nederlands")
 * @property {FlagImage} flagImage - Flag image file
 * @property {number|null} [registrationSurveyId] - Assigned registration survey ID
 * @property {string|null} [registrationSurveyName] - Assigned registration survey name
 * @property {number|null} [assignedTrainingProfileId] - Assigned training profile ID
 * @property {string|null} [assignedTrainingProfileName] - Assigned training profile name
 * @property {boolean} [assignedTrainingProfileIsPublished] - Whether the assigned training profile is currently published
 * @property {string} dateCreated - Creation timestamp
 * @property {string} dateModified - Last modified timestamp
 */

/**
 * @typedef {Object} CountryPagedResult
 * @property {Country[]} items - Array of countries
 * @property {number} totalCount - Total number of countries
 * @property {number} page - Current page number
 * @property {number} pageSize - Items per page
 * @property {number} totalPages - Total number of pages
 * @property {boolean} hasNextPage - Whether more pages exist
 * @property {boolean} hasPreviousPage - Whether previous pages exist
 */

/**
 * @typedef {Object} CountryQueryParams
 * @property {number} [page=1] - Page number
 * @property {number} [pageSize=50] - Items per page
 * @property {string} [search] - Search in name or language
 */

/**
 * Get all countries (no pagination, for dropdowns)
 * @param {Object} [options] - Request options
 * @returns {Promise<Country[]>}
 */
export const getAllCountries = async (options = {}) => {
  return api.get('/countries/all', {}, options);
};

/**
 * Get paginated list of countries
 * @param {CountryQueryParams} [params={}] - Query parameters
 * @param {Object} [options] - Request options
 * @returns {Promise<CountryPagedResult>}
 */
export const getCountries = async (params = {}, options = {}) => {
  return api.get('/countries', params, options);
};

/**
 * Get a single country by ID
 * @param {number} id - Country ID
 * @param {Object} [options] - Request options
 * @returns {Promise<Country>}
 */
export const getCountry = async (id, options = {}) => {
  return api.get(`/countries/${id}`, {}, options);
};

/**
 * Create a new country with flag image (requires admin)
 * Sends multipart/form-data with the flag image file
 * @param {Object} data - Country data
 * @param {string} data.name - Country name (required)
 * @param {string} data.language - Primary language (required)
 * @param {File} data.flagImage - Flag image file (required)
 * @param {Object} [options] - Request options
 * @returns {Promise<Country>}
 */
export const createCountry = async (data, options = {}) => {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('language', data.language);
  formData.append('flagImage', data.flagImage);

  return api.upload('/countries', formData, options);
};

/**
 * Update an existing country (requires admin)
 * Sends multipart/form-data, flag image is optional
 * @param {number} id - Country ID
 * @param {Object} data - Updated country data
 * @param {string} [data.name] - Country name
 * @param {string} [data.language] - Primary language
 * @param {File} [data.flagImage] - New flag image file (optional)
 * @param {Object} [options] - Request options
 * @returns {Promise<Country>}
 */
export const updateCountry = async (id, data, options = {}) => {
  const formData = new FormData();
  
  if (data.name) {
    formData.append('name', data.name);
  }
  if (data.language) {
    formData.append('language', data.language);
  }
  if (data.flagImage) {
    formData.append('flagImage', data.flagImage);
  }

  return api.uploadPut(`/countries/${id}`, formData, options);
};

/**
 * Delete a country (requires admin)
 * @param {number} id - Country ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteCountry = async (id, options = {}) => {
  return api.delete(`/countries/${id}`, options);
};

/**
 * @typedef {Object} CountryWithSurvey
 * @property {number} id - Country ID
 * @property {string} name - Country name
 * @property {string} language - Primary language
 * @property {string} [flagImageUrl] - Flag image URL
 * @property {number} registrationSurveyId - Assigned survey ID
 */

/**
 * Get countries that have registration surveys assigned (for registration page)
 * @param {Object} [options] - Request options
 * @returns {Promise<CountryWithSurvey[]>}
 */
export const getCountriesWithRegistrationSurveys = async (options = {}) => {
  return api.get('/countries/with-registration-surveys', {}, options);
};

/**
 * Assign or remove a registration survey from a country (requires admin)
 * @param {number} countryId - Country ID
 * @param {number|null} surveyId - Survey ID to assign, or null to remove
 * @param {Object} [options] - Request options
 * @returns {Promise<Country>}
 */
export const assignRegistrationSurvey = async (countryId, surveyId, options = {}) => {
  return api.put(`/countries/${countryId}/registration-survey`, { surveyId }, options);
};

/**
 * Assign or remove a training profile from a country (requires admin)
 * @param {number} countryId - Country ID
 * @param {number|null} trainingProfileId - Training profile ID to assign, or null to remove
 * @param {Object} [options] - Request options
 * @returns {Promise<Country>}
 */
export const assignTrainingProfile = async (countryId, trainingProfileId, options = {}) => {
  return api.put(`/countries/${countryId}/training-profile`, { trainingProfileId }, options);
};

/**
 * Default export with all country service methods
 */
export default {
  getAllCountries,
  getCountries,
  getCountry,
  createCountry,
  updateCountry,
  deleteCountry,
  getCountriesWithRegistrationSurveys,
  assignRegistrationSurvey,
  assignTrainingProfile,
};
